import api from './api';
import { TextAnalysis, AnalysisSection, AnalyzeTextRequest, PaginationParams } from '../types';

export const analysisService = {
  async analyzeText(data: AnalyzeTextRequest): Promise<{ analysis: TextAnalysis }> {
    const response = await api.post<{ analysis: TextAnalysis }>('/analysis/text', data);
    return response.data;
  },

  async analyzeDocument(formData: FormData): Promise<{ analysis: TextAnalysis }> {
    const response = await api.post<{ analysis: TextAnalysis }>('/analysis/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async analyzeUrl(data: { url: string; sectorIds: number[]; title?: string }): Promise<{ analysis: TextAnalysis }> {
    const response = await api.post<{ analysis: TextAnalysis }>('/analysis/url', data);
    return response.data;
  },

  async getAnalysisHistory(params?: PaginationParams): Promise<{
    sections?: AnalysisSection[];
    analyses?: TextAnalysis[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await api.get('/analysis', { params });
    return response.data;
  },

  async getAnalysisById(id: number): Promise<{ analysis: TextAnalysis }> {
    const response = await api.get<{ analysis: TextAnalysis }>(`/analysis/${id}`);
    return response.data;
  },

  async getSectionById(id: number): Promise<{ section: AnalysisSection }> {
    const response = await api.get<{ section: AnalysisSection }>(`/analysis/section/${id}`);
    return response.data;
  },

  async deleteAnalysis(id: number): Promise<void> {
    await api.delete(`/analysis/${id}`);
  },

  async simplifyText(id: number): Promise<{ simplifiedText: string }> {
    const response = await api.post<{ simplifiedText: string }>(`/analysis/${id}/simplify`);
    return response.data;
  },

  async getGroupedAnalyses(sourceType: 'DOCUMENT' | 'PDF' | 'URL' | 'MANUAL'): Promise<{
    analyses: Array<{
      title: string;
      sourceType: string;
      analysisId: number;
      sectionCount: number;
      createdAt: string;
      sector: any;
      auditStatus?: 'AUDITED' | 'UNAUDITED';
      analysisMetadata?: any;
      complianceStats?: {
        lenguajeClaro: {
          complies: boolean;
          percentage: number;
        };
        comprensibilidad: {
          complies: boolean;
          percentage: number;
        };
      };
      originalText?: string;
    }>;
    total: number;
  }> {
    const response = await api.get('/analysis/grouped', { params: { sourceType } });
    return response.data;
  },

  async getAnalysisSections(analysisId: number, params?: {
    page?: number;
    limit?: number;
    filter?: 'compliant' | 'non-compliant' | 'needs-glossary';
    auditStatus?: 'audited' | 'unaudited';
    search?: string;
  }): Promise<{
    analysis: {
      id: number;
      title: string;
      sourceType: string;
      createdAt: string;
      sector: any;
    };
    sections: AnalysisSection[];
    total: number;
    totalFiltered: number;
    page: number;
    limit: number;
    hasMore: boolean;
  }> {
    const response = await api.get(`/analysis/${analysisId}/sections`, { params });
    return response.data;
  },

  async simplifyTextContent(data: {
    text: string;
    targetLevel?: string;
    context?: string;
  }): Promise<{
    success: boolean;
    originalText: string;
    simplifiedText: string;
    provider: string;
    targetLevel: string;
  }> {
    const response = await api.post('/analysis/simplify', data);
    return response.data;
  },

  async simplifySectionText(sectionId: number): Promise<{
    success: boolean;
    sectionId: number;
    title: string;
    originalText: string;
    simplifiedText: string;
    provider: string;
    targetLevel: string;
  }> {
    const response = await api.post(`/analysis/section/${sectionId}/simplify`);
    return response.data;
  },

  async analyzeTextRealtime(data: {
    text: string;
    sectorIds?: number[];
  }): Promise<{
    infleszScore: number;
    avgWordsPerSentence: number;
    avgSyllablesPerWord: number;
    totalWords: number;
    totalSentences: number;
    vocabularyDistribution: Record<string, number>;
    b2Criteria: any;
    glossaryTerms: {
      count: number;
      terms: string[];
      breakdown: {
        c1Words: number;
        c2Words: number;
        acronyms: number;
      };
    };
  }> {
    const response = await api.post('/analysis/realtime', data);
    return response.data;
  },

  async updateCriterionValidations(analysisId: number, validations: { [key: string]: boolean | null }): Promise<{
    message: string;
    validations: { [key: string]: boolean | null };
  }> {
    const response = await api.patch(`/analysis/${analysisId}/validations`, { validations });
    return response.data;
  },

  async getCriterionValidations(analysisId: number): Promise<{
    validations: { [key: string]: boolean | null };
  }> {
    const response = await api.get(`/analysis/${analysisId}/validations`);
    return response.data;
  },

  async updateSectionValidations(sectionId: number, validations: { [key: string]: boolean | null }): Promise<{
    message: string;
    validations: { [key: string]: boolean | null };
  }> {
    const response = await api.patch(`/analysis/section/${sectionId}/validations`, { validations });
    return response.data;
  },

  async getSectionValidations(sectionId: number): Promise<{
    validations: { [key: string]: boolean | null };
  }> {
    const response = await api.get(`/analysis/section/${sectionId}/validations`);
    return response.data;
  },

  async downloadComplianceReport(analysisId: number): Promise<Blob> {
    const response = await api.get(`/analysis/${analysisId}/report`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async downloadGlossaryTerms(analysisId: number): Promise<Blob> {
    const response = await api.get(`/analysis/${analysisId}/export/glossary-terms`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async downloadSectionAnalysis(analysisId: number): Promise<Blob> {
    const response = await api.post(`/analysis/${analysisId}/export/sections`,
      {},
      { responseType: 'blob' }
    );
    return response.data;
  },

  async updateAnalysisWithEditedText(analysisId: number, text: string): Promise<{
    success: boolean;
    message: string;
    analysis: TextAnalysis;
  }> {
    const response = await api.put(`/analysis/${analysisId}/update-text`, { text });
    return response.data;
  },

  async updateSectionWithEditedContent(sectionId: number, text: string): Promise<{
    success: boolean;
    message: string;
    section: AnalysisSection;
  }> {
    const response = await api.put(`/analysis/section/${sectionId}/update-content`, { text });
    return response.data;
  },

  async toggleAuditStatus(analysisId: number): Promise<{
    success: boolean;
    message: string;
    analysis: TextAnalysis;
  }> {
    const response = await api.patch(`/analysis/${analysisId}/audit-status`);
    return response.data;
  },

  async toggleSectionAuditStatus(sectionId: number): Promise<{
    success: boolean;
    message: string;
    section: AnalysisSection;
  }> {
    const response = await api.patch(`/analysis/section/${sectionId}/audit-status`);
    return response.data;
  },
};
